import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, AiChatComposer, CHAT_COLUMN_MEASURE } from '@bliks-io/ui';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Slot at the start of the action row beneath the textarea (e.g. mic button) */
  leftSlot?: React.ReactNode;
  /** When true, the text input and send button are hidden and leftSlot fills the field */
  leftSlotExpanded?: boolean;
  /**
   * The send gate the caller computed — an empty draft, a still-loading history,
   * an uploading attachment. Enter and the button both obey it. Separate from
   * `disabled`, which additionally takes the textarea out of use.
   */
  canSend: boolean;
  /** Takes over a file-only paste; a plain-text paste is left to the browser */
  onPasteFiles?: (files: File[]) => void;
  /** Ref to the underlying textarea element */
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
}

/**
 * Shared chat input field with submit button.
 * Auto-grows with content up to ~6 lines, then scrolls.
 * Send on Enter (without Shift) or click the submit button.
 */
export function ChatInput({
  value,
  onChange,
  onSend,
  disabled,
  placeholder = 'Type a message...',
  className,
  leftSlot,
  leftSlotExpanded,
  canSend,
  onPasteFiles,
  textareaRef
}: ChatInputProps) {
  const { t } = useTranslation();

  const internalRef = useRef<HTMLTextAreaElement>(null);
  const mergedRef = textareaRef ?? internalRef;

  // Re-enabled e.g. once the AI response finishes.
  const wasDisabledRef = useRef(disabled);
  useEffect(
    function focusTextareaWhenEnabled() {
      if (wasDisabledRef.current && !disabled) {
        mergedRef.current?.focus();
      }
      wasDisabledRef.current = disabled;
    },
    [disabled, mergedRef]
  );

  // The centred cap pairs with the transcript's (`ChatMessages`): a full-page
  // host keeps composer and messages on one readable measure; inside the narrow
  // Bliksy panel it never binds.
  return (
    <div className={cn(CHAT_COLUMN_MEASURE, 'bg-background px-4 py-3', className)}>
      <AiChatComposer
        value={value}
        onChange={onChange}
        onSend={onSend}
        canSend={canSend}
        disabled={disabled}
        placeholder={placeholder}
        sendLabel={t('chat.sendMessage')}
        leftSlot={leftSlot}
        leftSlotExpanded={leftSlotExpanded}
        onPasteFiles={onPasteFiles}
        textareaRef={mergedRef}
      />
    </div>
  );
}
